import { Form, Formik } from 'formik';
import USER_TYPE from 'gcs-common/helper/userTypeHelper';
import { idValidatorRequired, stringValidator } from 'gcs-common/helper/yupValidationHelper';
import {
  CustomerResponsibilityMemberTypes,
  CustomerResponsibilityMemberTypesOptions,
} from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersConstants';
import { getCreateCustomerResponsibilityMemberErrorMessage } from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersSelector';
import { resetCreateCustomerResponsibilityMemberErrorMessage } from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersSlice';
import { createCustomerResponsibilityMember } from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersThunk';
import { getSelectedErpCustomer } from 'gcs-common/slices/administration/erpCustomers/erpCustomersSelectors';
import { erpCustomerSelectionReset } from 'gcs-common/slices/administration/erpCustomers/erpCustomersSlice';
import { fetchSelectedErpCustomer } from 'gcs-common/slices/administration/erpCustomers/erpCustomersThunks';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { showFailureNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import Button from '../../../Button/Button';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import CustomerResponsibilityRoleSelect from '../../shared/CustomerResponsibilityRoleSelect/CustomerResponsibilityRoleSelect';
import ErpCustomerSelect from '../../shared/ErpCustomerSelect/ErpCustomerSelect';
import FormikUserSelect from '../../shared/FormikUserSelect/FormikUserSelect';
import TeamSelect from '../../shared/TeamSelect/TeamSelect';
import styles from './styles.module.scss';

export const ResponsibilityMemberCreateValidationSchema = Yup.object().shape({
  erpCustomerId: idValidatorRequired,
  type: stringValidator,
  teamOrAgent: idValidatorRequired,
  role: idValidatorRequired,
});

const CustomerResponsibilityMemberCreate = ({ onCancel, onSuccess }) => {
  const [initialData, setInitialData] = useState({
    erpCustomerId: undefined,
    type: '',
    teamOrAgent: undefined,
    role: undefined,
  });
  const { id: erpCustomerId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const errorMessage = useSelector(getCreateCustomerResponsibilityMemberErrorMessage);
  const [disableErpCustomerSelect, setDisableErpCustomerSelect] = useState(false);
  const selectedErpCustomer = useSelector(getSelectedErpCustomer(erpCustomerId));

  useEffect(() => {
    (async () => {
      if (erpCustomerId && location.pathname.includes('erpCustomers')) {
        await dispatch(fetchSelectedErpCustomer({ erpCustomerId }));
        setInitialData((data) => ({
          ...data,
          erpCustomerId: erpCustomerId,
        }));
        setDisableErpCustomerSelect(true);
      } else {
        setInitialData((data) => ({
          ...data,
          erpCustomerId: undefined,
        }));
        setDisableErpCustomerSelect(false);
      }
    })();
    return () => dispatch(erpCustomerSelectionReset());
  }, [erpCustomerId, location, dispatch]);

  useEffect(() => {
    if (errorMessage) {
      dispatch(showFailureNotification(errorMessage, dispatch(resetCreateCustomerResponsibilityMemberErrorMessage())));
    }
  }, [dispatch, errorMessage]);

  useEffect(() => {
    dispatch(resetCreateCustomerResponsibilityMemberErrorMessage());
  }, [dispatch]);

  const submit = useCallback(
    async (values, { setSubmitting }) => {
      const result = await dispatch(createCustomerResponsibilityMember({ member: { ...values } }));
      setSubmitting(false);
      if (!result.error) {
        onSuccess(true);
      }
    },
    [dispatch, onSuccess],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialData}
      validationSchema={ResponsibilityMemberCreateValidationSchema}
      onSubmit={submit}
    >
      {({ isSubmitting, values }) => (
        <Form className={styles.column}>
          {erpCustomerId ? (
            <div className="my-2 flex flex-col gap-2">
              <div className="font-semibold text-neutral-11 uppercase">Kundennummer/Firmennummer</div>
              <div className="text-neutral-11">{`${selectedErpCustomer?.friendlyName} (${selectedErpCustomer?.customerNumber}) `}</div>
            </div>
          ) : (
            <div className={styles.row}>
              <ErpCustomerSelect
                label="Kundennummer/Firmennummer*"
                labelKey="nameWithNumber"
                valueKey="id"
                name="erpCustomerId"
                disabled={disableErpCustomerSelect}
              />
            </div>
          )}

          <div className={styles.row}>
            <FormikSelect
              options={CustomerResponsibilityMemberTypesOptions}
              label="Typ*"
              labelKey="label"
              valueKey="value"
              name="type"
            />
          </div>

          {values.type === CustomerResponsibilityMemberTypes.TEAM && (
            <div className={styles.row}>
              <TeamSelect label="Verkaufsmitarbeiter/Team*" labelKey="name" valueKey="id" name="teamOrAgent" />
            </div>
          )}
          {values.type === CustomerResponsibilityMemberTypes.INDIVIDUAL && (
            <div className={styles.row}>
              <FormikUserSelect
                selectKey="customerResponsibilityCreateAgentSelection"
                userType={USER_TYPE.AGENT}
                label="Verkaufsmitarbeiter/Team*"
                name="teamOrAgent"
                labelKey="friendlyName"
                valueKey="id"
              />
            </div>
          )}
          {values.type === CustomerResponsibilityMemberTypes.INDIVIDUAL && (
            <div className={styles.row}>
              <CustomerResponsibilityRoleSelect
                roleType={CustomerResponsibilityMemberTypes.INDIVIDUAL}
                label="Beteiligtenrolle*"
                labelKey="friendlyName"
                valueKey="id"
                name="role"
              />
            </div>
          )}
          {values.type === CustomerResponsibilityMemberTypes.TEAM && (
            <div className={styles.row}>
              <CustomerResponsibilityRoleSelect
                roleType={CustomerResponsibilityMemberTypes.TEAM}
                label="Beteiligtenrolle*"
                labelKey="friendlyName"
                valueKey="id"
                name="role"
              />
            </div>
          )}

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.buttonRow}>
            <Button className={styles.modalButtonSecondary} type="button" disabled={isSubmitting} onClick={onCancel}>
              Abbrechen
            </Button>
            <Button className={styles.modalButton} type="submit" disabled={isSubmitting}>
              Speichern
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

CustomerResponsibilityMemberCreate.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CustomerResponsibilityMemberCreate;
